import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useLocalLoginMutation, useMfaChallengeMutation, localApi } from '../../store/api/localApi';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  mfaChallengeIssued,
} from '../../store/slices/authSlice';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { brand } from '../../branding';

// Local-dev-only: the seeded-accounts quick-sign-in panel. Loaded via a lazy
// dynamic import behind a compile-time `import.meta.env.VITE_API_MODE` check
// so Vite/Rollup dead-code-eliminates both the import and the module from
// remote/prod bundles (the seeded credentials never ship to prod).
const IS_LOCAL_DEV = import.meta.env.VITE_API_MODE === 'local';
const DemoAccountsPanel = IS_LOCAL_DEV
  ? lazy(() => import('../../components/auth/DemoAccountsPanel'))
  : null;

const getLoginErrorMessage = (error: unknown): string => {
  const fallback = 'Login failed';

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const queryError = error as { data?: unknown; message?: unknown; error?: unknown };
  const data = queryError.data;

  if (typeof data === 'string') {
    return data;
  }

  if (data && typeof data === 'object') {
    const errorData = data as { message?: unknown; error?: unknown };
    if (typeof errorData.message === 'string') {
      return errorData.message;
    }
    if (typeof errorData.error === 'string') {
      return errorData.error;
    }
  }

  if (typeof queryError.message === 'string') {
    return queryError.message;
  }

  if (typeof queryError.error === 'string') {
    return queryError.error;
  }

  return fallback;
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [login] = useLocalLoginMutation();
  const [mfaChallenge] = useMfaChallengeMutation();
  // Set only when the password step returned an MFA challenge instead of
  // tokens. `session` lives here ONLY — never persisted to localStorage or
  // redux — and is cleared as soon as the code step succeeds, fails past
  // recovery, or the user goes back.
  const [mfaState, setMfaState] = useState<{ session: string; email: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ResetPassword.tsx redirects here with ?reset=1 after a successful change.
  // Showing a confirmation banner closes the loop so the user knows the new
  // password is the one to type below.
  const justResetPassword = searchParams.get('reset') === '1';
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await performLogin(email, password);
  };

  const performLogin = async (loginEmail: string, loginPassword: string) => {
    dispatch(loginStart());

    try {
      const result = await login({ email: loginEmail, password: loginPassword }).unwrap();
      // Narrow the union: login returns either tokens or an MFA challenge.
      if ('mfaRequired' in result) {
        setMfaState({ session: result.session, email: loginEmail });
        dispatch(mfaChallengeIssued());
        return;
      }
      dispatch(localApi.util.resetApiState());
      dispatch(loginSuccess(result));
    } catch (error: unknown) {
      dispatch(loginFailure(getLoginErrorMessage(error)));
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaState) return;

    setMfaSubmitting(true);
    setMfaError(null);
    try {
      const result = await mfaChallenge({
        email: mfaState.email,
        code: mfaCode,
        session: mfaState.session,
      }).unwrap();
      dispatch(localApi.util.resetApiState());
      dispatch(loginSuccess(result));
    } catch (error: unknown) {
      setMfaError(getLoginErrorMessage(error));
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleMfaBack = () => {
    setMfaState(null);
    setMfaCode('');
    setMfaError(null);
  };

  if (mfaState !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Enter your verification code
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Open your authenticator app and enter the 6-digit code for {mfaState.email}.
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleMfaSubmit}>
            {mfaError && (
              <div
                data-testid="mfa-code-error"
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
              >
                {mfaError}
              </div>
            )}
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">
                Verification code
              </label>
              <input
                id="mfa-code"
                name="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                data-testid="mfa-code-input"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm tracking-widest text-center"
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={mfaSubmitting || mfaCode.length !== 6}
                data-testid="mfa-code-submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mfaSubmitting ? 'Verifying...' : 'Verify code'}
              </button>
            </div>

            <div>
              <button
                type="button"
                data-testid="mfa-code-back"
                onClick={handleMfaBack}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to {brand.name}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Child management platform for schools and parents
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {justResetPassword && !error && (
            <div
              data-testid="login-reset-success"
              className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm"
            >
              Your password has been reset. Sign in with your new password.
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                data-testid="login-email"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                  className="absolute inset-y-0 right-0 z-20 pr-3 flex items-center"
                  onClick={() => setShowPassword((current) => !current)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>

        {/* Local-only quick-sign-in panel for seeded test accounts. The
            DemoAccountsPanel binding is null in non-local builds (and the
            lazy import is never reached), so Vite tree-shakes the module
            and its hardcoded seed credentials out of remote/prod bundles. */}
        {DemoAccountsPanel && (
          <Suspense fallback={null}>
            <DemoAccountsPanel onSelect={performLogin} disabled={loading} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default Login;
